import React from "react";
import { Link } from "react-router-dom";
import { Scale, ArrowLeft } from "lucide-react";
import { LandingFooter } from "@/components/landing/CtaBanner";

// The only contact address currently configured for the service (ADMIN_EMAIL).
const CONTACT_EMAIL = "supravat111@gmail.com";
const EFFECTIVE_DATE = "12 September 2026";

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`refund-section-${id}`} className="scroll-mt-24">
    <h2 className="font-display text-2xl font-semibold tracking-tight text-navy sm:text-[1.7rem]">{title}</h2>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-[0.95rem]">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="ml-1 space-y-2.5">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3">
        <span aria-hidden="true" className="mt-[0.5rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const TOC = [
  ["scope", "1. About this policy"],
  ["plans", "2. Plans, trial and pricing"],
  ["billing", "3. How billing works"],
  ["cancellation", "4. Cancelling a subscription"],
  ["after-cancel", "5. Access after cancellation"],
  ["trial-end", "6. What happens when the trial ends"],
  ["refunds", "7. Refund eligibility"],
  ["non-refundable", "8. Situations that are not refundable"],
  ["duplicate", "9. Duplicate or incorrect payments"],
  ["failed", "10. Failed or reversed payments"],
  ["how-to", "11. How to request a refund or billing correction"],
  ["timing", "12. Processing timelines"],
  ["downgrade", "13. Downgrades and plan changes"],
  ["payment-security", "14. Payment security"],
  ["related", "15. Related terms"],
  ["contact", "16. Contact"],
];

const PriceRow = ({ plan, monthly, yearly }) => (
  <div className="flex flex-col gap-1 rounded-md border border-border bg-secondary/40 p-4 sm:flex-row sm:items-center sm:justify-between">
    <p className="font-display text-base font-semibold text-navy">{plan}</p>
    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
      <span className="text-muted-foreground">
        Monthly: <strong className="text-navy">{monthly}</strong>
      </span>
      <span className="text-muted-foreground">
        Yearly: <strong className="text-navy">{yearly}</strong>
      </span>
    </div>
  </div>
);

export default function RefundPolicy() {
  return (
    <div data-testid="refund-policy-page" className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="refund-brand" className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-navy text-gold">
              <Scale className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <span className="font-display text-xl tracking-tight text-navy">Advocate Diary</span>
          </Link>
          <Link
            to="/"
            data-testid="refund-back-home"
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy/70 transition-colors hover:bg-secondary hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
        </div>
      </header>

      <div className="border-b border-border bg-navy px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold">Legal</p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Cancellation &amp; Refund Policy
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            How trials, subscription cancellations and refunds are handled on Advocate Diary.
          </p>
          <p className="mt-6 text-xs text-slate-400" data-testid="refund-effective-date">
            Effective date: {EFFECTIVE_DATE}
          </p>
        </div>
      </div>

      <main className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[220px_minmax(0,1fr)]">
          <nav data-testid="refund-toc" className="hidden lg:block">
            <div className="sticky top-24">
              <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
                On this page
              </p>
              <ul className="mt-4 space-y-2 border-l border-border pl-4">
                {TOC.map(([id, label]) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      data-testid={`refund-toc-${id}`}
                      className="block text-[13px] leading-snug text-muted-foreground transition-colors hover:text-navy"
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-6 border-l border-border pl-4">
                <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
                  Related
                </p>
                <Link
                  to="/terms"
                  data-testid="refund-toc-terms"
                  className="mt-3 block text-[13px] leading-snug text-muted-foreground transition-colors hover:text-navy"
                >
                  Terms of Service
                </Link>
                <Link
                  to="/privacy-policy"
                  data-testid="refund-toc-privacy"
                  className="mt-2 block text-[13px] leading-snug text-muted-foreground transition-colors hover:text-navy"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </nav>

          <article className="max-w-3xl space-y-12">
            <Section id="scope" title="1. About this policy">
              <p>
                This Cancellation &amp; Refund Policy explains how trials, subscription cancellations and
                refund requests are handled on the Advocate Diary application. It applies to all paying
                subscribers of Advocate Diary.
              </p>
              <p>
                Cancelling a subscription and being eligible for a refund are two different things. You can
                cancel a subscription at any time from inside the application; whether any amount already paid
                can be refunded depends on the situation, as described below.
              </p>
              <p>
                This policy forms part of, and should be read together with, our{" "}
                <Link
                  to="/terms"
                  data-testid="refund-inline-terms"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy-policy"
                  data-testid="refund-inline-privacy"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </Section>

            <Section id="plans" title="2. Plans, trial and pricing">
              <p>
                Every new account starts with a <strong className="text-navy">30-day free trial</strong> of the
                paid features. No payment is taken to start the trial, and no payment instrument details are
                required. You can use Advocate Diary during the trial without being charged.
              </p>
              <p>
                After the trial, the following paid plans are available:
              </p>
              <div className="space-y-3">
                <PriceRow plan="Pro Advocate" monthly="₹499 / month" yearly="₹4,990 / year" />
                <PriceRow plan="Law Firm" monthly="₹999 / month" yearly="₹9,990 / year" />
              </div>
              <p>
                Prices are shown in Indian Rupees (INR). Applicable taxes, if any, are added at checkout by the
                payment gateway. Plan features, limits and prices are shown inside the application at the time
                you subscribe and are the ones that apply to your purchase.
              </p>
            </Section>

            <Section id="billing" title="3. How billing works">
              <p>
                Subscriptions are processed through <strong className="text-navy">Razorpay</strong>, a payment
                gateway. When you subscribe, you are taken to Razorpay&rsquo;s checkout to complete the payment.
              </p>
              <Bullets
                items={[
                  "Advocate Diary does not store your card number, CVV, UPI PIN, net banking credentials or any other payment instrument credentials. These are entered into and handled by Razorpay, not by Advocate Diary.",
                  "The price applicable to your plan is the price shown in the application at the time you subscribe. Applicable taxes may be added at checkout.",
                  "Your subscription renews automatically for the billing cycle you selected — monthly or yearly — unless it is cancelled before the next renewal.",
                  "Advocate Diary stores subscription information — such as the plan you are on, the status of the subscription, and payment records returned by the gateway — so that your account, receipts and access to features are correct.",
                ]}
              />
            </Section>

            <Section id="cancellation" title="4. Cancelling a subscription">
              <p>
                You can cancel a paid subscription at any time from{" "}
                <strong className="text-navy">Settings inside the application</strong>. Cancellation stops
                further renewals of your plan.
              </p>
              <p>
                Cancellation takes effect at the end of the current billing period — that is, at the end of the
                month or year you have already paid for. You are not charged again for that plan after
                cancellation, unless you resubscribe.
              </p>
              <p>
                Cancellation by itself does not automatically issue a refund. Whether any amount already paid
                can be refunded is covered by the sections below.
              </p>
            </Section>

            <Section id="after-cancel" title="5. Access after cancellation">
              <p>
                After you cancel a paid subscription, you continue to have access to the paid features until
                the end of the billing period you have already paid for. Once that period ends, the paid
                features are no longer available on your account.
              </p>
              <Bullets
                items={[
                  "Your records — cases, clients, hearings, tasks, documents — remain on your account so that you can sign in and see them, subject to any storage or plan limits that apply outside the paid features.",
                  "If you have chosen to have your account and data removed, that is handled separately as described in our Privacy Policy; cancellation does not by itself delete your data.",
                  "If a paid feature has plan-based limits and the plan ends, use of that feature returns to the free-plan limits.",
                ]}
              />
            </Section>

            <Section id="trial-end" title="6. What happens when the trial ends">
              <p>
                The 30-day free trial ends automatically after 30 days from account creation. No payment is
                taken because no payment instrument is collected to start the trial.
              </p>
              <Bullets
                items={[
                  "If you subscribe to a paid plan before or at the end of the trial, your account moves onto that plan and the paid billing cycle applies.",
                  "If you do not subscribe, your account continues on the free plan and the paid-only features are no longer available. Your records remain on the account.",
                  "Because no payment is taken to start or continue the trial, there is nothing to refund at the end of a trial.",
                ]}
              />
            </Section>

            <Section id="refunds" title="7. Refund eligibility">
              <p>
                Refunds are not automatic. A refund is considered where the payment or the situation clearly
                warrants one. Typical situations we will look at include:
              </p>
              <Bullets
                items={[
                  "A duplicate charge — for example, the same subscription period charged twice.",
                  "An incorrect charge — for example, a charge for a plan you did not select, or an amount that does not match the plan shown at checkout.",
                  "A charge processed after you cancelled the subscription and the cancellation should have prevented it.",
                  "A technical failure of the service that prevented you from being able to use the paid features you paid for, where the issue is on our side and has not been resolved.",
                ]}
              />
              <p>
                Where a refund is issued, it is refunded to the original payment method through Razorpay.
                Refunds cannot be issued to a different card, UPI ID, bank account or wallet than the one used
                for the original payment.
              </p>
              <p>
                We do not commit to a fixed refund percentage or to a guaranteed refund on any subscription.
                Each request is looked at on its own facts.
              </p>
            </Section>

            <Section id="non-refundable" title="8. Situations that are not refundable">
              <p>
                Some payments are not refundable. This includes, but is not limited to:
              </p>
              <Bullets
                items={[
                  "Amounts for a billing period that has already been used, in whole or in part — for example, mid-cycle cancellations of an already-paid month or year. Cancellation stops future renewals but does not refund the period you have already been on the plan.",
                  "Change of mind after the 30-day free trial has ended and the subscription has been paid for.",
                  "Non-use of the service during a paid billing period, where the service itself has remained available.",
                  "Requests made a long time after the payment in question, where records with the payment gateway are no longer available in a form we can act on.",
                  "Amounts that must be retained to meet a legal or tax obligation.",
                  "Any add-on or one-time fee described as non-refundable in the application at the time of purchase.",
                ]}
              />
              <p>
                The 30-day free trial gives you time to evaluate Advocate Diary before you pay. We encourage
                you to use the trial to confirm the service fits your practice before subscribing.
              </p>
            </Section>

            <Section id="duplicate" title="9. Duplicate or incorrect payments">
              <p>
                If you notice a duplicate charge — the same subscription period billed twice — or an amount
                that does not match the plan you selected, contact us with the details listed under
                &ldquo;How to request a refund or billing correction&rdquo; below.
              </p>
              <p>
                We will check the payment records against your subscription and, where a duplicate or an
                incorrect charge is confirmed, arrange a refund of the extra amount to the original payment
                method through Razorpay, or a billing correction against your account, whichever is
                appropriate.
              </p>
            </Section>

            <Section id="failed" title="10. Failed or reversed payments">
              <p>
                Payments can sometimes fail or be reversed at the bank or payment-method level — for example, a
                declined card, an insufficient balance, or a reversal initiated by the bank. In such cases:
              </p>
              <Bullets
                items={[
                  "No amount is due to be refunded from Advocate Diary where the payment did not actually complete on the gateway side.",
                  "Access to the paid features depends on whether the payment ultimately succeeded. If the payment did not succeed, the paid features may not be activated even though a charge attempt was shown on your statement.",
                  "If your bank or card statement shows a charge but the payment is not reflected on your Advocate Diary account, check with your bank first — pending or authorised amounts often reverse on their own. If the amount does not reverse, contact us with the details in the next section so we can check with the payment gateway.",
                ]}
              />
            </Section>

            <Section id="how-to" title="11. How to request a refund or billing correction">
              <p>
                To request a refund, or to report a billing issue, email us at{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="refund-inline-contact"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  {CONTACT_EMAIL}
                </a>{" "}
                from the email address registered on your Advocate Diary account, so that we can be reasonably
                satisfied the request comes from you.
              </p>
              <p>Please include:</p>
              <Bullets
                items={[
                  "The email address on the account.",
                  "The plan you subscribed to (Pro Advocate or Law Firm) and whether it is monthly or yearly.",
                  "The approximate date and amount of the payment in question.",
                  "Any Razorpay payment or order reference you have (usually visible on the receipt email or on your bank statement).",
                  "A short description of what you would like us to look at — refund, cancellation, duplicate charge, incorrect amount, failed payment, and so on.",
                ]}
              />
              <p>
                We may ask you for further information to confirm the account and the payment before acting on
                a request.
              </p>
            </Section>

            <Section id="timing" title="12. Processing timelines">
              <p>
                We aim to acknowledge a refund or billing request within a reasonable time after we receive it,
                and to complete the review as soon as practicable. Where a refund is approved, the refund is
                initiated to the original payment method through Razorpay.
              </p>
              <p>
                The time it takes for a refunded amount to appear back on your card, bank account, UPI or
                wallet is controlled by the bank or payment method used — Advocate Diary does not control that
                timing. We do not publish or promise a guaranteed refund-arrival timeline, because the exact
                time depends on the bank and the payment method involved.
              </p>
            </Section>

            <Section id="downgrade" title="13. Downgrades and plan changes">
              <p>
                A subscription can be downgraded or cancelled from Settings inside the application.
              </p>
              <Bullets
                items={[
                  "A downgrade or cancellation stops further renewals at the higher plan.",
                  "You continue to have access to the plan you have already paid for until the end of the current billing period. A downgrade or cancellation does not, by itself, refund the amount already paid for the current period.",
                  "Where a downgrade means your usage would exceed the lower plan's limits, the lower plan's limits will apply from the effective date of the downgrade.",
                  "Switching between monthly and yearly billing takes effect at the end of the current billing period, unless the application shows otherwise at the time of the change.",
                ]}
              />
            </Section>

            <Section id="payment-security" title="14. Payment security">
              <p>
                All payments are processed through Razorpay. Advocate Diary does not store your card number,
                CVV, UPI PIN, net banking credentials or any other payment instrument credentials — those are
                entered into and handled by Razorpay, not by Advocate Diary.
              </p>
              <p>
                Advocate Diary stores subscription and payment records — such as the plan you are on, the
                status of the subscription, and payment references returned by the gateway — so that receipts,
                cancellations and refunds can be linked to the right account. Razorpay&rsquo;s own handling of
                your payment details is governed by Razorpay&rsquo;s terms and privacy policy.
              </p>
            </Section>

            <Section id="related" title="15. Related terms">
              <p>
                This policy should be read together with our{" "}
                <Link
                  to="/terms"
                  data-testid="refund-inline-terms-2"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy-policy"
                  data-testid="refund-inline-privacy-2"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Privacy Policy
                </Link>
                . Where the Terms of Service and this policy address the same subject, this policy applies to
                the specific question of cancellation and refunds, and the Terms of Service apply to everything
                else.
              </p>
              <p>
                We may update this policy as the service changes. The effective date at the top of the page
                shows when the current version took effect. Continuing to use the service after an update
                means the updated policy applies to your use of it.
              </p>
            </Section>

            <Section id="contact" title="16. Contact">
              <p>For questions about cancellations, refunds or billing, contact us at:</p>
              <p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="refund-contact-email"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p>
                Please write from the email address registered on your account. No telephone number or postal
                address is published for billing support, and none should be assumed. Email is the contact
                route for the service.
              </p>
            </Section>

            <div className="rounded-lg border border-border bg-secondary/60 p-5">
              <p className="text-xs leading-relaxed text-muted-foreground">
                This page describes the cancellation and refund practices of the Advocate Diary application as
                currently operated. It intentionally does not state a legal entity name, registered address,
                telephone number, guaranteed refund timeline or fixed refund percentage, because none of those
                has been established for the service. This page will be updated if that changes. Please also
                read our{" "}
                <Link
                  to="/terms"
                  data-testid="refund-footer-terms"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy-policy"
                  data-testid="refund-footer-privacy"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </article>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
